import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';

interface Tenant {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  mobile: string;
  street: string;
  street2: string;
  city: string;
  zip: string;
  state: string;
  civility: string;
  image?: string;
  user_id: number;
  OwnerId: number;
}

interface TenantState {
  tenants: Tenant[];
  tenant: Tenant | null;
  activeTenantId: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: TenantState = {
  tenants: [],
  tenant: null,
  activeTenantId: null,
  loading: false,
  error: null,
};

const safeAppend = (formData: FormData, key: string, value: any) => {
  if (value !== undefined && value !== null && value !== '') {
    formData.append(key, value);
  }
};

export const fetchTenants = createAsyncThunk(
  'tenant/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const ownerId = state.authentification.user.OwnerId;
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/tenants/all/${ownerId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenants');
    }
  }
);

export const fetchTenant = createAsyncThunk(
  'tenant/fetchOne',
  async (tenantId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/tenants/${tenantId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      dispatch(fetchTenants());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenant');
    }
  }
);

export const createTenant = createAsyncThunk(
  'tenant/create',
  async (tenantData: FormData | any, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const ownerId = state.authentification.user.OwnerId;

      console.log('🔍 createTenant - Type de données:', tenantData instanceof FormData ? 'FormData' : 'Object');
      console.log('🔍 OwnerId depuis Redux:', ownerId);

      // ✅ Si tenantData est déjà un FormData (depuis le composant), on l'utilise directement
      let formData: FormData;
      
      if (tenantData instanceof FormData) {
        console.log('✅ Utilisation du FormData existant');
        formData = tenantData;
        
        // ✅ S'assurer que OwnerId est présent et correct
        if (!formData.has('OwnerId') || formData.get('OwnerId') === 'undefined') {
          if (formData.has('OwnerId')) {
            formData.delete('OwnerId');
          }
          formData.append('OwnerId', String(ownerId));
          console.log('✅ OwnerId ajouté/corrigé dans FormData:', ownerId);
        }

        // ✅ Debug: Afficher le contenu du FormData
        console.log('📦 Contenu FormData:');
        for (let [key, value] of formData.entries()) {
          console.log(`   ${key}:`, value instanceof File ? `File(${value.name})` : value);
        }
      } else {
        // ✅ Sinon on crée un nouveau FormData (cas legacy)
        console.log('🆕 Création nouveau FormData');
        formData = new FormData();
        safeAppend(formData, "civility", tenantData.civility);
        safeAppend(formData, "firstname", tenantData.firstname);
        safeAppend(formData, "lastname", tenantData.lastname);
        safeAppend(formData, "phone", tenantData.phone);
        safeAppend(formData, "mobile", tenantData.mobile);
        safeAppend(formData, "street", tenantData.street);
        safeAppend(formData, "street2", tenantData.street2);
        safeAppend(formData, "city", tenantData.city);
        safeAppend(formData, "zip", tenantData.zip);
        safeAppend(formData, "state", tenantData.state);
        safeAppend(formData, "email", tenantData.email);
        safeAppend(formData, "birthdate", tenantData.birthdate);
        safeAppend(formData, "birthplace", tenantData.birthplace);
        safeAppend(formData, "image", tenantData.image);
        safeAppend(formData, "user_id", tenantData.user_id);
        safeAppend(formData, "OwnerId", String(ownerId));
      }

      const response = await axios.post(`${baseUrlApi}/tenants/`, formData, {
        headers: {
          Authorization: `bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      console.log('✅ Tenant créé avec succès:', response.data);
      dispatch(fetchTenants());
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur createTenant:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || "L'ajout du tenant a échoué !");
    }
  }
);

export const updateTenant = createAsyncThunk(
  'tenant/update',
  async ({ tenantData, tenantId }: { tenantData: any; tenantId: number }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;

      console.log('🔍 updateTenant - ID:', tenantId);
      console.log('🔍 Type de données:', tenantData instanceof FormData ? 'FormData' : 'Object');

      const response = await axios.put(`${baseUrlApi}/tenants/${tenantId}`, tenantData, {
        headers: { 
          Authorization: `bearer ${token}`,
          ...(tenantData instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {})
        }
      });

      // ✅ Gérer l'image séparément si présente (pour compatibilité backend)
      if (tenantData instanceof FormData && tenantData.has('image')) {
        const imageFile = tenantData.get('image');
        if (imageFile instanceof File) {
          const photoFormData = new FormData();
          photoFormData.append("photos", imageFile);

          await axios.post(`${baseUrlApi}/tenants/${tenantId}/pictures/`, photoFormData, {
            headers: {
              Authorization: `bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            }
          });
          console.log('✅ Image uploadée séparément');
        }
      }

      console.log('✅ Tenant mis à jour avec succès');
      dispatch(fetchTenants());
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur updateTenant:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || "La mise à jour du tenant a échoué !");
    }
  }
);

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    encoursTenant: (state, action: PayloadAction<any>) => {
      state.activeTenantId = action.payload.id || action.payload;
      state.tenant = action.payload;
    },
    resetTenantState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants = action.payload;
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(fetchTenant.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.tenant = action.payload;
      })
      .addCase(fetchTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(createTenant.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants.push(action.payload);
      })
      .addCase(createTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(updateTenant.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTenant.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tenants.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tenants[index] = action.payload;
        }
      })
      .addCase(updateTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { encoursTenant, resetTenantState } = tenantSlice.actions;
export default tenantSlice.reducer;